import { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from '../hooks/useAuth';
import { authedFetch } from '../lib/api';
import { ErrorBanner } from './ErrorBanner';
import { VERIFY_ATTEMPT_EVENT, type VerifyAttemptDetail } from './ManualVerifyModal';

type VerifyChannel = 'web' | 'whatsapp' | 'fb_messenger' | 'email';

interface Props {
  tenantCode: string;
  chatId: string;
  /**
   * Opens the agent-assisted last-4 modal. REQUIRED (not optional) on purpose:
   * this used to be a `window.dispatchEvent('chatolam:open-manual-verify')` that
   * nothing listened for, so the button was a silent no-op. Keeping it a
   * mandatory prop makes the compiler refuse a call site that forgets to wire it.
   */
  onManualVerify: () => void;
}

interface StatusResp {
  locked: boolean;
  lockedUntil: number | null;
  attempts: number;
  attemptsRemaining: number;
}

/** services/verify-lockout.ts MAX_ATTEMPTS — 3 strikes, then a 24h lock. */
const MAX_ATTEMPTS = 3;

/**
 * What the badge is allowed to claim, and the address the claim is about.
 *
 * The counter is stored per (email, channel), so a number without its email is
 * not a fact. This panel used to render `3 - attemptsRemaining` polled for
 * `chat.contactEmail` and label it "N of 3 attempts used" flat out — which read
 * "0 of 3" straight after two agent attempts against a different address the
 * agent had typed into the verify modal.
 */
interface AttemptView {
  /** null ⇒ counted against an address the bridge resolved, which we weren't told. */
  email: string | null;
  attemptsUsed: number;
  locked: boolean;
  lockedUntil: number | null;
}

interface ChatState {
  channel: VerifyChannel;
  authLevel?: string;
  contactEmail?: string;
}

const REASON_OPTIONS = [
  'Confirmed identity by phone callback',
  'Confirmed identity by government ID upload',
  'Other (specify)',
];

function channelOfChat(raw?: string): VerifyChannel {
  const c = (raw ?? 'web').toLowerCase();
  if (c === 'whatsapp') return 'whatsapp';
  if (c === 'fb_messenger' || c === 'messenger' || c === 'fb') return 'fb_messenger';
  if (c === 'email') return 'email';
  return 'web';
}

export function WpVerifyControls({ tenantCode, chatId, onManualVerify }: Props) {
  const { user } = useAuth();
  const [chat, setChat] = useState<ChatState | null>(null);
  const [status, setStatus] = useState<StatusResp | null>(null);
  /** The status poll failed — we know nothing, which is not the same as zero. */
  const [statusFailed, setStatusFailed] = useState(false);
  /** Bumped to re-run the poll; setStatus(null) alone never refetched anything. */
  const [statusNonce, setStatusNonce] = useState(0);
  /** Counters the verify modal got back from the server on its last attempt. */
  const [lastAttempt, setLastAttempt] = useState<VerifyAttemptDetail | null>(null);
  const [busy, setBusy] = useState(false);
  // Holds either a caught error or a local validation string; ErrorBanner takes both.
  const [error, setError] = useState<unknown>(null);
  const [requestSent, setRequestSent] = useState(false);
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [reasonChoice, setReasonChoice] = useState(REASON_OPTIONS[0]);
  const [reasonText, setReasonText] = useState('');

  // Subscribe to chat doc.
  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'chats', chatId), (snap) => {
      const data = snap.data() as {
        channel?: string;
        authLevel?: string;
        contactEmail?: string;
      } | undefined;
      setChat({
        channel: channelOfChat(data?.channel),
        authLevel: data?.authLevel,
        contactEmail: data?.contactEmail,
      });
    });
    return unsub;
  }, [chatId]);

  // Counters belong to a chat's attempt, never to the next chat we open.
  useEffect(() => {
    setLastAttempt(null);
    const onAttempt = (e: Event) => {
      const detail = (e as CustomEvent<VerifyAttemptDetail>).detail;
      if (!detail || detail.chatId !== chatId) return;
      setLastAttempt(detail);
    };
    window.addEventListener(VERIFY_ATTEMPT_EVENT, onAttempt);
    return () => window.removeEventListener(VERIFY_ATTEMPT_EVENT, onAttempt);
  }, [chatId]);

  // Fetch lockout status when email + channel known.
  useEffect(() => {
    if (!user || !chat?.contactEmail || chat.authLevel === 'verified') {
      setStatus(null);
      setStatusFailed(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        // Best-effort: a failure here just leaves the lockout counters unknown.
        const data = await authedFetch<StatusResp>(
          `/agent/wp/verify/status?tenantCode=${encodeURIComponent(tenantCode)}&email=${encodeURIComponent(chat.contactEmail!)}&channel=${chat.channel}`,
        );
        if (!cancelled) { setStatus(data); setStatusFailed(false); }
      } catch {
        // Say so rather than falling back to a zero the server never sent.
        if (!cancelled) { setStatus(null); setStatusFailed(true); }
      }
    })();
    return () => { cancelled = true; };
  }, [tenantCode, chat?.contactEmail, chat?.channel, chat?.authLevel, user, statusNonce]);

  if (!chat) return null;

  // Verified — quiet green badge, no controls.
  if (chat.authLevel === 'verified') {
    return (
      <div className="px-4 py-2 text-[11px] text-green-700 bg-green-50 border-l-2 border-green-400">
        ✓ Identity verified
      </div>
    );
  }

  // A live attempt beats the poll: it came back from the write that moved the
  // counter, and it carries the address the counter actually moved on — which
  // is whatever the agent typed in the modal, not necessarily chat.contactEmail.
  const attemptCounters = lastAttempt?.counters ?? null;
  const attemptView: AttemptView | null = attemptCounters
    ? {
        email: lastAttempt?.email ?? null,
        attemptsUsed: Math.max(0, MAX_ATTEMPTS - attemptCounters.attemptsRemaining),
        locked: attemptCounters.locked,
        lockedUntil: attemptCounters.lockedUntil,
      }
    : status && chat.contactEmail
      ? {
          email: chat.contactEmail,
          attemptsUsed: status.attempts,
          locked: status.locked,
          lockedUntil: status.lockedUntil,
        }
      : null;

  const lockedUntilStr = attemptView?.lockedUntil ? new Date(attemptView.lockedUntil).toLocaleString() : null;
  const onWhat = attemptView?.email ? ` on ${attemptView.email}` : ' on the matched account';
  /**
   * The address any lockout/override applies to — NOT always the chat's email.
   * When the counters came from a phone-only attempt the server never told us
   * which account it keyed them to, so this stays null rather than guessing:
   * unlocking the wrong key clears somebody else's lock and leaves this one up.
   */
  const verifyEmail = attemptCounters ? lastAttempt?.email ?? null : chat.contactEmail ?? null;

  const requestVerify = async () => {
    if (!user) return;
    setBusy(true); setError(null);
    try {
      await authedFetch(`/agent/chat/${encodeURIComponent(chatId)}/request-verify`, {
        method: 'POST',
        body: JSON.stringify({ tenantCode }),
      });
      setRequestSent(true);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };

  const performOverride = async () => {
    if (!user) return;
    // The lock is keyed on the address that failed, which is the one the agent
    // typed into the modal when that differs from the chat's contact email.
    // Sending chat.contactEmail would clear a lock on somebody else.
    if (!verifyEmail) { setError('No email to unlock — the lock is keyed per address'); return; }
    const reason = reasonChoice === 'Other (specify)' ? reasonText.trim() : reasonChoice;
    if (!reason || reason.length < 4) { setError('Reason required (min 4 chars)'); return; }
    setBusy(true); setError(null);
    try {
      await authedFetch('/agent/wp/verify/unlock', {
        method: 'POST',
        body: JSON.stringify({ tenantCode, chatId, email: verifyEmail, channel: chat.channel, reason }),
      });
      setOverrideOpen(false);
      setLastAttempt(null);
      setStatus(null);
      setStatusNonce((n) => n + 1); // actually refetch — setStatus(null) never did
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="px-4 py-3 text-xs text-gray-700">
      <div className="font-semibold uppercase tracking-wide text-gray-500 mb-1.5">Identity verification</div>

      <div className="flex items-center justify-between mb-2">
        <span className="text-[11px]">
          {attemptView?.locked ? (
            <span className="text-red-700">
              🔒 LOCKED{onWhat}{lockedUntilStr ? ` until ${lockedUntilStr}` : ''}
            </span>
          ) : attemptView ? (
            <span className="text-amber-700">
              ✗ Not verified — {attemptView.attemptsUsed} of {MAX_ATTEMPTS} failed attempts{onWhat}
            </span>
          ) : statusFailed ? (
            <span className="text-amber-700">✗ Not verified — attempt count unavailable</span>
          ) : chat.contactEmail ? (
            <span className="text-amber-700">✗ Not verified — checking attempts…</span>
          ) : (
            // The old copy said "0 of 3 attempts used" here, which is a claim
            // about a counter that does not exist until there is an address.
            <span className="text-amber-700">✗ Not verified — attempts are counted per email; none known yet</span>
          )}
        </span>
      </div>

      {lastAttempt && !lastAttempt.counters && (
        <div className="text-[10px] text-gray-500 mb-2 leading-snug">
          {lastAttempt.uncounted === 'no_key'
            ? 'Your last attempt was NOT counted against the 3-try lockout — there was no email to count it against.'
            : 'Your last attempt was NOT counted — the card check never ran.'}
        </div>
      )}

      {!chat.contactEmail && (
        <div className="text-[11px] text-gray-500 mb-2">
          No email on this chat yet — ask the customer for it, or use Verify (last-4).
        </div>
      )}

      <ErrorBanner error={error} className="text-[11px] text-red-600 mb-1" />

      <div className="flex flex-wrap gap-1.5">
        <button
          type="button"
          disabled={busy || requestSent}
          onClick={requestVerify}
          title="Prompts the customer to verify themselves: the web widget shows a last-4 card form; on WhatsApp/email the assistant walks them through it. Use this when you can't take the digits yourself."
          className="text-[11px] px-2 py-1 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
        >
          {requestSent ? 'Request sent ✓' : 'Request customer to verify'}
        </button>
        <button
          type="button"
          onClick={onManualVerify}
          title="Opens the last-4 form so you can enter the digits the customer reads out. The card is checked against the real account."
          className="text-[11px] px-2 py-1 border border-gray-300 rounded hover:bg-gray-50"
        >
          Verify (last-4)
        </button>
        {attemptView?.locked && (
          <button
            type="button"
            onClick={() => setOverrideOpen(true)}
            className="text-[11px] px-2 py-1 border border-red-300 text-red-700 rounded hover:bg-red-50"
          >
            Override lockout
          </button>
        )}
      </div>
      <p className="text-[10px] text-gray-400 mt-1.5 leading-snug">
        <span className="font-medium">Request customer to verify</span> asks the customer to confirm identity
        themselves; <span className="font-medium">Verify (last-4)</span> lets you enter the card's last 4 digits
        with them on the line. Either way the digits are checked against the real account.
      </p>

      {overrideOpen && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => !busy && setOverrideOpen(false)}>
          <div className="bg-white rounded-lg shadow-lg p-4 w-full max-w-md text-xs" onClick={(e) => e.stopPropagation()}>
            <div className="font-semibold text-sm mb-2">Override lockout</div>
            <p className="text-gray-600 mb-3">
              This clears the 24-hour lock on <span className="font-mono">{verifyEmail ?? '(no address)'}</span> for the
              <span className="font-medium"> {chat.channel}</span> channel. Audit-logged with your name and reason.
            </p>
            <label className="block mb-1 text-gray-700">Reason</label>
            <select
              value={reasonChoice}
              onChange={(e) => setReasonChoice(e.target.value)}
              className="w-full border border-gray-300 rounded px-2 py-1 mb-2"
            >
              {REASON_OPTIONS.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
            {reasonChoice === 'Other (specify)' && (
              <textarea
                value={reasonText}
                onChange={(e) => setReasonText(e.target.value)}
                placeholder="Describe how identity was confirmed…"
                className="w-full border border-gray-300 rounded px-2 py-1 mb-2 h-20"
              />
            )}
            <div className="flex justify-end gap-2 mt-2">
              <button type="button" disabled={busy} onClick={() => setOverrideOpen(false)} className="px-3 py-1 border border-gray-300 rounded">Cancel</button>
              <button type="button" disabled={busy} onClick={performOverride} className="px-3 py-1 bg-red-600 text-white rounded disabled:opacity-50">
                {busy ? 'Unlocking…' : 'Confirm override'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
